import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { AccountBalance } from '@mui/icons-material';
import { PaymentMethodType } from 'skipify-types';

import { CardOnFileCell } from './CardOnFileCell';
import { TransactionPaymentMethod } from '../../store/modules/orders/types';
import flexPayLogo from '../../images/flex_pay/flex_pay_horizontal_logo.svg';

interface Props {
  paymentMethod: TransactionPaymentMethod;
  isFlexPay?: boolean | null;
  variant?: 'default' | 'compact';
}

const formatAccountType = (accountType: string) => {
  switch (accountType.toLowerCase()) {
    case 'checking':
      return 'Checking';
    case 'savings':
      return 'Savings';
    default:
      return accountType;
  }
};

export const PaymentMethodCell = ({ paymentMethod, isFlexPay, variant = 'default' }: Props) => {
  const {
    paymentMethodType = PaymentMethodType.CARD,
    networkType,
    lastFour,
    bankName,
    accountType,
  } = paymentMethod;

  const isBankAccount = paymentMethodType === PaymentMethodType.BANK_ACCOUNT;
  const isCard =
    paymentMethodType === PaymentMethodType.CARD && !isFlexPay && networkType && lastFour;

  if (!isBankAccount && !isCard && !isFlexPay) {
    return null;
  }

  if (isFlexPay) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <Box
          sx={{
            width: variant === 'compact' ? '72px' : '93px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={flexPayLogo}
            alt="FlexPay"
            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </Box>
      </Stack>
    );
  }

  if (isBankAccount) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <Box
          sx={{
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'grey.100',
            borderRadius: '4px',
            flexShrink: 0,
          }}
        >
          <AccountBalance sx={{ color: 'grey.600', fontSize: 20 }} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            ...(variant === 'default' && {
              flexDirection: 'row',
              gap: 0.5,
              alignItems: 'center',
            }),
            ...(variant === 'compact' && {
              flexDirection: 'column',
              gap: 0,
              alignItems: 'flex-start',
              width: '40px',
              pb: 0.25,
            }),
          }}
        >
          <Typography component="span" sx={{ fontSize: variant === 'compact' ? '10px' : '12px' }}>
            {bankName || 'Bank Account'}
          </Typography>
          {accountType && (
            <Typography
              component="span"
              sx={{ fontSize: variant === 'compact' ? '9px' : '11px', color: 'grey.600' }}
            >
              {formatAccountType(accountType)}
            </Typography>
          )}
        </Box>
      </Stack>
    );
  }

  // Card display
  return <CardOnFileCell networkType={networkType} lastFour={lastFour} variant={variant} />;
};
